import growthBook from 'services/growthBook';
import { hasReactNativeBridge, isNativeAppWebView } from 'utils/webview';
import {
  isGontijoSessionExpired,
  readGontijoSession,
} from '@/services/gontijo-session/gontijoSessionStorage';

/**
 * The one brand this flag is built for.
 *
 * `LOGIN_ON_CHECKOUT` does not turn on a generic account step: it turns on a sign-in against
 * Gontijo's own ERP. For anybody else it would be a gate whose credentials none of their buyers can
 * hold — a checkout nobody can pay.
 */
const GONTIJO_BRAND = 'gontijo';

/**
 * Whether the tenant asks for the account on the payment screen.
 *
 * The non-hook twin of `useExperimentalFeature('LOGIN_ON_CHECKOUT')`, for the payload builders,
 * which are plain functions in actions and cannot call hooks. It resolves the same two sources in
 * the same order — GrowthBook first, whitelabel config as the fallback — and appends the brand the
 * same way `useGrowthBookFeatureValue` does, lowercased.
 *
 * Deliberately not `growthBook.getGrowthBookFeature`, which appends the brand *without* lowercasing:
 * for a tenant whose config spells the brand with a capital, that helper and the hook would resolve
 * two different feature keys, and the flag would read as on in components and off in the payload —
 * a purchase that shows the gate and then pays without the token.
 * @param {Object} state - Redux state.
 * @returns {boolean} True when checkout login is enabled for this tenant.
 */
export const selectLoginOnCheckoutEnabled = (state) => {
  const brand = (state?.whitelabelConfig?.env?.brand || '').toLowerCase();

  // The GrowthBook half is already brand-scoped — enabling it elsewhere would mean deliberately
  // creating `login_on_checkout_<someone else>`. The whitelabel half is not: `LOGIN_ON_CHECKOUT` set
  // on a root config reaches every brand underneath it, and each one would render the Gontijo gate
  // and hold its pay button for a session it has no way to obtain. Making "Gontijo only" true here
  // is cheaper than trusting that no configuration ever says otherwise.
  if (brand !== GONTIJO_BRAND) return false;

  let fromGrowthBook;
  if (brand && growthBook.isInitialized()) {
    fromGrowthBook = growthBook.getInstance().getFeatureValue(`login_on_checkout_${brand}`);
  }

  return (fromGrowthBook ?? state?.whitelabelConfig?.features?.LOGIN_ON_CHECKOUT) === true;
};

/**
 * Which brand's session, issued by which backend.
 * @param {Object} state - Redux state.
 * @returns {{brand: string, purchaseUrl: string}} The storage scope.
 */
export const selectGontijoSessionScope = (state) => ({
  brand: state?.whitelabelConfig?.env?.brand || '',
  purchaseUrl: state?.whitelabelConfig?.env?.api?.purchaseUrl || '',
});

/**
 * The session, as anything outside a component should read it.
 *
 * Falls back to storage when the store has not been hydrated yet. Without that, everything that
 * runs before the provider mounts — a payload built from a deep link straight into checkout, say —
 * would see no session and quietly build an anonymous purchase for a buyer who is signed in.
 * @param {Object} state - Redux state.
 * @returns {{token: ?string, expiresAt: ?number, email: ?string, isHydrated: boolean, isValidating: boolean, isAuthenticated: boolean}} The session.
 */
export const selectGontijoSession = (state) => {
  const slice = state?.gontijoSession;
  const isHydrated = slice?.get('isHydrated') === true;
  const source = isHydrated
    ? { token: slice.get('token'), expiresAt: slice.get('expiresAt'), email: slice.get('email') }
    : readGontijoSession(selectGontijoSessionScope(state));

  const token = source?.token || null;
  const expiresAt = source?.expiresAt ?? null;
  const email = source?.email || null;

  return {
    token,
    expiresAt,
    email,
    isHydrated,
    isValidating: slice?.get('isValidating') === true,
    isAuthenticated: Boolean(token) && !isGontijoSessionExpired({ token, expiresAt, issuedAt: 0 }),
  };
};

/**
 * The `client_auth` to put in a request body, or null.
 *
 * The one place that answers this question. Null unless the flag is on for this tenant *and* there
 * is a session that has not expired — so a payload builder never has to know about either, and a
 * brand without the flag cannot grow a `client_auth` key by accident.
 * @param {Object} state - Redux state.
 * @returns {?string} The token, or null when none should travel.
 */
export const selectGontijoClientAuth = (state) => {
  if (!selectLoginOnCheckoutEnabled(state)) return null;

  const { token, isAuthenticated } = selectGontijoSession(state);

  return isAuthenticated ? token : null;
};

/**
 * Whether payment must be held because the buyer has no Gontijo session.
 *
 * For Gontijo there is no guest purchase: without `client_auth` the backend issues no ticket and
 * takes no money, so letting the pay button through would only produce a failure the buyer cannot
 * act on. Every other brand answers false here and is untouched.
 *
 * Exchange is not excluded, and that is deliberate. Those buyers arrive from Mis Viajes already
 * signed in, so in practice this never fires for them — but if their session has expired, the
 * backend still refuses the charge, and holding the button is the honest response. What exchange
 * does skip is being offered a sign-in, which `GontijoCheckoutLoginButton` handles.
 *
 * Never inside the native app's WebView. There the app owns the session and stamps the purchase
 * itself (`POST purchases/:token/gontijo/client-auth`) before `/secure/cielo/:token` pays, so the
 * funnel holding no session says nothing. This is the one owner of that exemption: the checkout
 * guards and `gontijoSessionPrecondition` all ask here. The user-agent tag alone is not enough: the
 * app's payment WebView does not send it, so the react-native-webview bridge counts as well.
 *
 * Also held while `validate_token` is still out on a restored session. Until it answers, the token
 * is a claim, and a 3D Secure return is exactly a boot with one. The hold is bounded:
 * `revalidateGontijoSession` waits through `readGontijoSessionVerdictWithDeadline`
 * (`SESSION_VERDICT_TIMEOUT_MS`), and every outcome clears `isValidating`.
 * @param {Object} state - Redux state.
 * @returns {boolean} True when payment must not be launched.
 */
export const selectGontijoPaymentBlocked = (state) => {
  if (!selectLoginOnCheckoutEnabled(state)) return false;
  if (isNativeAppWebView() || hasReactNativeBridge()) return false;

  const { isAuthenticated, isValidating } = selectGontijoSession(state);

  return !isAuthenticated || isValidating;
};
